import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuickAction } from '@/hooks/useQuickAction';
import type { ModalType } from '@/hooks/useQuickAction';
import { useAuth } from '@/contexts/AuthContext';
import GlobalSearch from './GlobalSearch';

interface HeaderProps {
  collapsed: boolean;
  onToggle: () => void;
}

const quickActions = [
  { label: 'Yeni Satış', icon: 'ri-shopping-cart-line', color: 'bg-accent-500', desc: 'Yeni satış kaydı oluştur' },
  { label: 'Yeni Müşteri', icon: 'ri-user-add-line', color: 'bg-primary-500', desc: 'Müşteri kaydı ekle' },
  { label: 'Ödeme Al', icon: 'ri-bank-card-line', color: 'bg-amber-400', desc: 'Müşteriden ödeme al' },
  { label: 'Yeni Ürün Ekle', icon: 'ri-box-3-line', color: 'bg-secondary-500', desc: 'Ürün stoğuna ekle' },
  { label: 'Teslimat Planla', icon: 'ri-truck-line', color: 'bg-primary-400', desc: 'Teslimat planlaması yap' },
  { label: 'Gider Ekle', icon: 'ri-money-dollar-circle-line', color: 'bg-red-400', desc: 'Gider kaydı oluştur' },
];

export default function Header({ collapsed, onToggle }: HeaderProps) {
  const { openModal } = useQuickAction();
  const { profile, user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const quickRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (quickRef.current && !quickRef.current.contains(e.target as Node)) {
        setShowQuickActions(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfile(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const today = new Date().toLocaleDateString('tr-TR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  function handleAction(label: string) {
    setShowQuickActions(false);
    const typeMap: Record<string, ModalType> = {
      'Yeni Satış': 'sale',
      'Yeni Müşteri': 'customer',
      'Ödeme Al': 'payment',
      'Yeni Ürün Ekle': 'product',
      'Teslimat Planla': 'delivery',
      'Gider Ekle': 'expense',
    };
    openModal(typeMap[label]);
  }

  const handleLogout = async () => {
    setLoggingOut(true);
    setShowProfile(false);
    const result = await signOut();
    if (result.success) {
      navigate('/login', { replace: true });
    } else {
      setLoggingOut(false);
    }
  };

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'Kullanıcı';
  const displayEmail = user?.email || '';

  return (
    <header className="sticky top-0 z-30 h-16 bg-background-50 border-b border-background-200/70 flex items-center justify-between px-4 md:px-6"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div className="flex items-center gap-3">
        {/* Hamburger for mobile */}
        <button
          onClick={onToggle}
          className="w-9 h-9 lg:hidden flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer"
          style={{ minWidth: 44, minHeight: 44 }}
          aria-label="Menü"
        >
          <i className="ri-menu-line text-xl"></i>
        </button>
        {/* Desktop collapse toggle */}
        <button
          onClick={onToggle}
          className="w-9 h-9 hidden lg:flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 cursor-pointer"
          style={{ minWidth: 44, minHeight: 44 }}
        >
          <i className={`text-lg ${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'}`}></i>
        </button>
        <div className="flex items-center lg:hidden">
          <img
            src="https://storage.readdy-site.link/project_files/3da75122-8c1c-4e05-bb8b-0bbdfa25568f/ec53652a-062b-4b3c-bc4c-a094b3839457_polatlar-yonetim-paneli.png"
            alt="Polatlar Yönetim Paneli"
            className="h-8 w-auto object-contain shrink-0"
          />
        </div>
        <GlobalSearch />
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        <div className="hidden md:block text-right leading-tight">
          <p className="text-sm font-semibold text-foreground-800">Merhaba, {displayName.split(' ')[0]}</p>
          <p className="text-xs text-foreground-400">{today}</p>
        </div>

        {/* Quick Actions Dropdown */}
        <div className="relative" ref={quickRef}>
          <button
            onClick={() => setShowQuickActions(!showQuickActions)}
            className="btn-primary flex items-center gap-2 !px-3 !py-2 cursor-pointer"
          >
            <i className={`ri-flashlight-line text-sm ${showQuickActions ? 'animate-pulse' : ''}`}></i>
            <span className="hidden sm:inline text-xs font-medium">Hızlı İşlem</span>
            <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${showQuickActions ? 'rotate-180' : ''}`}></i>
          </button>

          {showQuickActions && (
            <div className="absolute right-0 top-full mt-2 w-72 bg-background-50 rounded-2xl border border-background-200 shadow-xl py-2 z-50 overflow-hidden animate-dropdown-in">
              <div className="px-4 py-2 border-b border-background-100">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Hızlı İşlemler</p>
              </div>
              <div className="py-1">
                {quickActions.map((action) => (
                  <button
                    key={action.label}
                    onClick={() => handleAction(action.label)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-all duration-150 group"
                  >
                    <span className={`w-8 h-8 rounded-lg ${action.color} flex items-center justify-center flex-shrink-0 transition-transform duration-150 group-hover:scale-110`}>
                      <i className={`${action.icon} text-white text-sm`}></i>
                    </span>
                    <div className="flex flex-col items-start min-w-0">
                      <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{action.label}</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{action.desc}</span>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300 ml-auto opacity-0 group-hover:opacity-100 transition-opacity"></i>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Profile */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setShowProfile(!showProfile)}
            className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-primary-200 transition-all"
          >
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt={displayName} className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <i className="ri-user-3-line text-primary-600 text-sm"></i>
            )}
          </button>
          {showProfile && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-background-50 rounded-xl border border-background-200 shadow-lg py-1.5 z-50 animate-dropdown-in">
              <div className="px-4 py-3 border-b border-background-100">
                <p className="text-sm font-medium text-foreground-800">{displayName}</p>
                <p className="text-xs text-foreground-400">{displayEmail}</p>
              </div>
              <button
                onClick={() => { setShowProfile(false); navigate('/settings'); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-user-settings-line"></i> Profilim
              </button>
              <button
                onClick={() => { setShowProfile(false); navigate('/settings'); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-settings-3-line"></i> Ayarlar
              </button>
              <button
                onClick={handleLogout}
                disabled={loggingOut}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loggingOut ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> Çıkış yapılıyor...
                  </>
                ) : (
                  <>
                    <i className="ri-logout-box-line"></i> Çıkış Yap
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}